import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@trello/nachos/button';

interface UnconfirmedUserTextProps {
  willJoinOnConfirm: boolean;
  fxQueueJoin: () => void;
}

export const UnconfirmedUserText: FunctionComponent<
  UnconfirmedUserTextProps
> = ({ willJoinOnConfirm, fxQueueJoin }) => {
  const [showJoinOnConfirmMessage, setShowJoinOnConfirmMessage] =
    useState(false);

  const handleQueueAutoJoin = useCallback(() => {
    fxQueueJoin();

    setShowJoinOnConfirmMessage(true);
  }, [fxQueueJoin]);

  if (!showJoinOnConfirmMessage) {
    return (
      <>
        <p className="invitation-link-explanation">
          <FormattedMessage
            id="templates.invitation_link.before-you-can-join-you-ll-need-to-confirm-your-email-address"
            // eslint-disable-next-line formatjs/no-multiple-whitespaces
            defaultMessage="… but before you can join, you'll need to confirm your email address.  (We sent you a link when you signed up; hopefully it's arrived by now!)"
            description="Confirm email address"
          />
        </p>
        {!willJoinOnConfirm && (
          <div className="invitation-link-options">
            <Button
              className="nch-button nch-button--primary"
              onClick={handleQueueAutoJoin}
            >
              <FormattedMessage
                id="templates.invitation_link.join-after-i-confirm-my-email"
                defaultMessage="Join after I confirm my email"
                description="Join after email confirmation"
              />
            </Button>
          </div>
        )}
      </>
    );
  }

  // Technically, this should only show if `!willJoinOnConfirm`
  // is true, but if we've reached this point, that means
  // that `showJoinOnConfirmMessage` is true and that can only
  // be set to true if `handleQueueAutoJoin` has been triggered
  // and that can only be triggered if `!willJoinOnConfirm` is true
  return (
    <p className="invitation-link-explanation">
      <FormattedMessage
        id="templates.invitation_link.youll-be-added-as-soon-as-you-confirm-your-email"
        defaultMessage="You'll be added as soon as you confirm your email!"
        description="Confirm email address"
      />
    </p>
  );
};
